import * as API from "../api"
import displayFlash from "partners/scripts/display_flash"
import { stripExtraFields } from "../reducers/applet"
import buildAppletFromTandas from "../lib/build_applet_from_tandas"
import logAction from "../../../utils/log_action"
import uniq from "lodash/uniq"
import InlineErrors from "shared/components/inline_errors"

export function newApplet(isMaker) {
  return {
    type: "NEW_APPLET",
    isMaker,
  }
}

// Given an applet, we still need to load data about its associated services
// before we can display the UI.
export function loadAppletDependencies(applet, isMaker) {
  return dispatch => {
    const promises = []
    let serviceNames = []

    const triggerServiceName = applet.applet_trigger.channel_module_name

    if (triggerServiceName) {
      promises.push(loadTriggersForService(triggerServiceName)(dispatch))
      serviceNames.push(triggerServiceName)
    }

    const triggerModuleName = applet.applet_trigger.module_name
    const triggerFqn = triggerModuleName && `${triggerServiceName}.${triggerModuleName}`

    uniq((applet.applet_queries || []).map(as => as.channel_module_name)).forEach(moduleName => {
      if (moduleName) {
        promises.push(loadQueriesForService(moduleName)(dispatch))
        serviceNames.push(moduleName)
      }
    })
    uniq((applet.applet_actions || []).map(as => as.channel_module_name)).forEach(moduleName => {
      if (moduleName) {
        promises.push(loadActionsForService(moduleName, triggerFqn)(dispatch))
        serviceNames.push(moduleName)
      }
    })

    serviceNames = uniq(serviceNames)
    promises.push(loadServices(serviceNames)(dispatch))

    Promise.all(promises).then(results => {
      // If we couldn't load all of the relevant services, we need to show an
      // error message, because it means the applet relies on a preview
      // channel that the current user can't access.
      if (results.pop().length < serviceNames.length) {
        dispatch({ type: "MISSING_PREVIEW_ACCESS" })
      } else {
        dispatch({
          type: "APPLET_LOADED",
          applet,
          isMaker,
        })
      }
    })
  }
}

export function loadTandasAsTemplate(triggerModuleName, actionModuleName, isMaker) {
  return dispatch => {
    API.loadTandas(triggerModuleName, actionModuleName).then(tandas => {
      loadAppletDependencies(buildAppletFromTandas(tandas, "", "", isMaker))(dispatch)
    })
  }
}

export function loadServicesAction(services) {
  return {
    type: "SERVICES_LOADED",
    services,
  }
}

export function loadServices(serviceNames) {
  return dispatch => {
    return API.loadServices(serviceNames).then(services => {
      dispatch(loadServicesAction(services))
      return services
    })
  }
}

export function updateAppletMetadata(data) {
  return {
    type: "UPDATE_APPLET_METADATA",
    data,
  }
}

export function selectTriggerService(service) {
  return dispatch => {
    dispatch({
      type: "SELECT_TRIGGER_SERVICE",
      service,
    })

    loadTriggersForService(service.module_name)(dispatch)
  }
}

export function loadTriggersForService(serviceModuleName) {
  return dispatch => {
    return API.loadTriggers(serviceModuleName).then(triggers => {
      dispatch({
        type: "TRIGGERS_LOADED",
        serviceModuleName,
        triggers,
      })
    })
  }
}

export function selectQueryService(index, service) {
  return dispatch => {
    dispatch({
      type: "SELECT_QUERY_SERVICE",
      service,
      index,
    })

    loadQueriesForService(service.module_name)(dispatch)
  }
}

export function loadQueriesForService(serviceModuleName) {
  return dispatch => {
    return API.loadQueries(serviceModuleName).then(queries => {
      dispatch({
        type: "QUERIES_LOADED",
        serviceModuleName,
        queries,
      })
    })
  }
}

export function selectActionService(index, service, currentTrigger) {
  return dispatch => {
    dispatch({
      type: "SELECT_ACTION_SERVICE",
      service,
      index,
    })

    loadActionsForService(service.module_name, currentTrigger && currentTrigger.id)(dispatch)
  }
}

export function loadActionsForService(serviceModuleName, currentTriggerId) {
  return dispatch => {
    return API.loadActions(serviceModuleName, currentTriggerId).then(actions => {
      dispatch({
        type: "ACTIONS_LOADED",
        serviceModuleName,
        actions,
      })
    })
  }
}

export function selectAction(index, action, defaults) {
  return {
    type: "SELECT_ACTION",
    index,
    action,
    defaults,
  }
}

export function selectQuery(index, query) {
  return {
    type: "SELECT_QUERY",
    index,
    query,
  }
}

export function selectTrigger(trigger, previoslySelectedActions = []) {
  return dispatch => {
    dispatch({
      type: "SELECT_TRIGGER",
      trigger,
    })
    previoslySelectedActions.forEach(appletAction =>
      loadActionsForService(appletAction.channel_module_name, trigger.id)(dispatch)
    )
  }
}

export function updateTriggerField(fieldIndex, field, data) {
  return {
    type: "UPDATE_TRIGGER_FIELD",
    field,
    data,
  }
}

export function updateQueryField(queryIndex, fieldIndex, field, data) {
  return {
    type: "UPDATE_QUERY_FIELD",
    index: queryIndex,
    field,
    data,
  }
}

export function updateActionField(actionIndex, fieldIndex, field, data) {
  return {
    type: "UPDATE_ACTION_FIELD",
    index: actionIndex,
    field,
    data,
  }
}

export function addQuery() {
  return {
    type: "ADD_QUERY",
  }
}

export function removeQuery(index) {
  return {
    type: "REMOVE_QUERY",
    index,
  }
}

export function addAction() {
  return {
    type: "ADD_ACTION",
  }
}

export function removeAction(index) {
  return {
    type: "REMOVE_ACTION",
    index,
  }
}

export function removeTrigger() {
  return {
    type: "REMOVE_TRIGGER",
  }
}

export function invalidFieldResolution(field, error) {
  return {
    type: "INVALID_FIELD_RESOLUTION",
    field,
    error,
  }
}

export function incompatibleTrigger(actionService, incompatibleTrigger) {
  return {
    type: "INCOMPATIBLE_TRIGGER",
    actionService,
    incompatibleTrigger,
  }
}

export function clearIncompatibilityError() {
  return {
    type: "CLEAR_INCOMPATIBILITY_ERROR",
  }
}

export function createApplet(applet, appletsURL, isSuggested, updateURL) {
  return dispatch => {
    const [id, payload] = stripExtraFields(applet)
    const successMessage = applet.id ? "Changes saved" : "Applet Created"
    const failureMessage = `Could not ${id ? "update" : "create"} Applet. Please check your data.`

    window.uiEventTracker.trackUserInteraction("panda.applets.compose.preview.clicked", {
      service_id: window.App.pageViewData.service_id,
      user_id: window.App.pageViewData.user_id,
    })

    API.createOrUpdateApplet(payload, id, updateURL)
      .then(({ applet }) => {
        displayFlash(successMessage)

        if (isSuggested) {
          logAction("created_suggested_applet", {
            applet_slug: applet.id,
            service_slug: applet.applet_trigger.channel_module_name,
          })
        }
        window.uiEventTracker.trackUserInteraction("panda.applets.compose.success", {
          service_id: window.App.pageViewData.service_id,
          user_id: window.App.pageViewData.user_id,
        })

        window.Turbolinks.visit(`${appletsURL}/${applet.id}`)
      })
      .catch(reason => {
        window.uiEventTracker.trackUserInteraction("panda.applets.compose.failure", {
          service_id: window.App.pageViewData.service_id,
          user_id: window.App.pageViewData.user_id,
        })

        displayFlash(failureMessage, "error")
        setTimeout(InlineErrors.scrollToFirstInstance, 0)
        dispatch({
          type: "APPLET_CREATE_OR_UPDATE_FAILED",
          reason,
        })
      })

    dispatch({ type: "CREATE_OR_UPDATE_APPLET" })
  }
}
